<template>
  <c-flex
    position="relative"
    align-items="center"
    v-chakra="{
      '.is-loading ~ .loading-spinner': {
        display: 'inherit',
      },
      '.expired ~ .is-expired': {
        display: 'inherit',
      },
      '.valid ~ .is-valid': {
        display: 'inherit',
      },
      '.invalid ~ .not-found': {
        display: 'inherit',
      },
      '.old-product ~ .too-old-product': {
        display: 'inherit',
      },
      '.loading-spinner, .is-expired, .not-found, .is-valid, .too-old-product': {
        padding: 0,
        display: 'none',
        position: 'absolute',
        top: '1rem',
        right: '1rem',
        cursor: 'pointer',
        transform: 'translateY(-50%)',
      },
      '.is-expired, .not-found, .is-valid, .too-old-product': {
        top: '50%',
        transform: 'translateY(-50%)',
      },
      'input.old-product': {
        borderColor: 'red.300',
        borderWidth: '2px',
      },
    }"
    :data-cy="`cr-product-serial-wrapper-${inputNumber}`"
  >
    <input
      :id="inputId"
      v-model.trim="itemSerial"
      @load="debouncedOnSerialNumberChange"
      @blur="clearDebounceAndTriggerSerialNumberChange"
      :disabled="disableInput"
      type="text"
      v-chakra
      px="4"
      height="12"
      :my="{ base: '0', sm: 'xs' }"
      font-size="1rem !important"
      width="100%"
      border-radius="input"
      class="serial-input"
      :data-cy="`cr-product-serial-${inputNumber}`"
    />
    <spinner size="sm" />
    <c-tooltip
      hasArrow
      p="sm"
      border-radius="input"
      :label="
        $t('productReturn.productEditor.serialNumber.expired', {
          date: formattedWarrantyEnd(itemSerial),
        })
      "
    >
      <c-text as="span" class="is-expired" tabindex="0">
        <c-icon name="alert" color="brand.orange" size="1.4rem" data-cy="is-expired-icon" />
      </c-text>
    </c-tooltip>
    <c-tooltip
      hasArrow
      p="sm"
      border-radius="input"
      :label="$t('productReturn.productEditor.serialNumber.notFound')"
    >
      <c-text as="span" class="not-found" tabindex="0">
        <c-icon name="alert" color="brand.red" size="1.4rem" data-cy="not-found-icon" />
      </c-text>
    </c-tooltip>
    <c-tooltip
      hasArrow
      p="sm"
      border-radius="input"
      :label="$t('productReturn.productEditor.serialNumber.oldProduct')"
    >
      <c-text as="span" class="too-old-product" tabindex="0">
        <c-icon name="alert" color="brand.red" size="1.4rem" data-cy="old-product-icon" />
      </c-text>
    </c-tooltip>
    <c-tooltip
      hasArrow
      p="sm"
      border-radius="input"
      :label="
        $t('productReturn.productEditor.serialNumber.valid', {
          date: formattedWarrantyEnd(itemSerial),
        })
      "
    >
      <c-text as="span" class="is-valid" tabindex="0">
        <c-icon name="info" color="brand.green" size="1.4rem" data-cy="is-valid-icon" />
      </c-text>
    </c-tooltip>
  </c-flex>
</template>

<script lang="ts">
import Spinner from "@/components/Spinner.vue";
import { CButton, CFlex, CIcon, CInput, CText, CTooltip } from "@chakra-ui/vue";
import { IWarrantyInfo, Logger } from "@iloq/shared";
import { debounce, DebouncedFunc } from "lodash";
import moment from "moment-timezone";
import { defineComponent } from "vue";
import { WarrantyInfoNotFoundError } from "../../WarrantyInfoNotFoundError";

enum SNInputState {
  valid = "valid",
  expired = "expired",
  invalid = "invalid",
  loading = "is-loading",
  oldProduct = "old-product",
}

export default defineComponent({
  name: "RMACRSerialNumber",
  components: {
    Spinner,
    CTooltip,
    CText,
    CButton,
    CInput,
    CFlex,
    CIcon,
  },
  props: {
    itemSerial: {
      type: String,
      required: true,
    },
    disableInput: {
      type: Boolean,
      default: false,
      required: false,
    },
    inputNumber: {
      type: Number,
      required: true,
    },
    inputId: {
      type: String,
      required: true,
    },
    returnDate: {
      type: String,
      default: () => moment().format("YYYY-MM-DD"),
      required: false,
    },
  },
  data() {
    return {
      debouncedOnSerialNumberChange: undefined as DebouncedFunc<any> | undefined,
      loadingSerialCount: 0,
      blockingSerialCount: 0,
      warrantyInfos: {} as Record<string, IWarrantyInfo>,
      notFoundWarrantyInfos: [] as string[],
    };
  },
  beforeMount() {
    this.debouncedOnSerialNumberChange = debounce(this.onSerialNumberChange, 1000);
    this.initHelperVariablesOnEdit();
  },
  methods: {
    initHelperVariablesOnEdit(): void {
      this.loadingSerialCount = 0;
      this.blockingSerialCount = 0;
    },
    clearDebounceAndTriggerSerialNumberChange(input: any): void {
      if (this.debouncedOnSerialNumberChange) {
        this.debouncedOnSerialNumberChange.cancel();
      }
      this.onSerialNumberChange(input);
    },
    async getWarrantyInfoForSerialNumber(serialNumber: string): Promise<IWarrantyInfo> {
      return this.$store.dispatch("returns/getWarrantyInfo", serialNumber);
    },
    warrantyInfo(sn?: string): IWarrantyInfo | undefined {
      return sn ? this.warrantyInfos[sn] : undefined;
    },
    async getWarrantyInfo(sn: string): Promise<IWarrantyInfo> {
      const warrantyInfo = this.warrantyInfo(sn);

      if (warrantyInfo) {
        return warrantyInfo;
      }

      if (this.notFoundWarrantyInfos.includes(sn)) {
        throw new WarrantyInfoNotFoundError("Warranty end not found");
      }

      const newWarrantyInfo = await this.getWarrantyInfoForSerialNumber(sn);

      this.$set(this.warrantyInfos, sn, newWarrantyInfo);

      return newWarrantyInfo;
    },
    formattedWarrantyEnd(serialNumber?: string): string | undefined {
      const warrantyInfo = this.warrantyInfo(serialNumber);
      return warrantyInfo && moment(warrantyInfo.warrantyEnd).format("D.M.YYYY");
    },
    clearSerialNumberFieldStyles(input: HTMLInputElement, ignoreBlockingSerialCount: boolean) {
      if (!ignoreBlockingSerialCount && input.classList.contains(SNInputState.oldProduct)) {
        this.blockingSerialCount--;
      }
      input.classList.remove(...Object.values(SNInputState));
    },
    addStateStyle(input: HTMLInputElement, state: SNInputState) {
      input.classList.add(state);
    },
    clearLoadingAndAddState(input: HTMLInputElement, state: SNInputState) {
      input.classList.remove(SNInputState.loading);
      this.addStateStyle(input, state);
    },
    async onSerialNumberChange(
      { target: input }: { target: HTMLInputElement },
      fakeChange = false
    ) {
      try {
        if (!this.$store?.state?.features?.warrantyinfo) {
          return;
        }

        const serialNumber = input?.value?.trim();

        this.clearSerialNumberFieldStyles(input, fakeChange);

        if (!serialNumber) {
          return;
        }

        this.addStateStyle(input, SNInputState.loading);
        this.loadingSerialCount++;

        const { warrantyEnd } = await this.getWarrantyInfo(serialNumber);

        if (moment(this.returnDate).isBefore(moment(warrantyEnd))) {
          this.clearLoadingAndAddState(input, SNInputState.valid);
        } else if (moment().subtract(3, "y").isAfter(moment(warrantyEnd))) {
          this.clearLoadingAndAddState(input, SNInputState.oldProduct);
          this.blockingSerialCount++;
        } else {
          this.clearLoadingAndAddState(input, SNInputState.expired);
        }
        this.loadingSerialCount--;
      } catch (err) {
        if (!(err instanceof WarrantyInfoNotFoundError)) {
          Logger.error(err);
        }
        this.notFoundWarrantyInfos.push(input?.value);
        this.clearLoadingAndAddState(input, SNInputState.invalid);
        this.loadingSerialCount--;
      }
    },
  },
  mounted() {
    this.onSerialNumberChange(
      { target: document.querySelector(`#${this.inputId}`) as HTMLInputElement },
      true
    );
  },
});
</script>
